import { Box, InputAdornment, MenuItem, Popover, Stack } from "@mui/material";
import IconButton from "../ui/IconButton";
import Input from "../ui/Input";
import LoadingButton from "../ui/LoadingButton";
import Select from "../ui/Select";
import { AccessTimeOutlined } from "@mui/icons-material";
import { useMemo, useState } from "react";
import type { SxProps, Theme } from "@mui/material/styles";
import { useIntl } from "../../contexts/IntlContext";

type Props = {
  label: string;
  value: string; // HH:mm (24h)
  onChange: (value: string) => void;
  onNow?: () => void;
  onClear?: () => void;
  /** HH:mm; horários anteriores ficam desabilitados (use só no dia em que o limite vale). */
  minTime?: string;
  /** HH:mm; horários posteriores ficam desabilitados. */
  maxTime?: string;
  minuteStep?: number; // minutes between options, default 5
  minWidth?: number;
  sx?: SxProps<Theme>;
  size?: "small" | "medium";
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  fullWidth?: boolean;
};

type Period = "AM" | "PM";

const pad2 = (n: number) => String(n).padStart(2, "0");

const to12h = (hour24: number): { hour12: number; period: Period } => {
  const period: Period = hour24 >= 12 ? "PM" : "AM";
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { hour12, period };
};

const to24h = (hour12: number, period: Period): number => {
  const base = hour12 % 12;
  return period === "PM" ? base + 12 : base;
};

const toMinutes = (hhmm?: string): number | null => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(hhmm ?? "");
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

const nowHHmm = () => {
  const now = new Date();
  return `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
};

export default function TimePickerField({
  label,
  value,
  onChange,
  onNow,
  onClear,
  minTime,
  maxTime,
  minuteStep = 5,
  minWidth = 160,
  sx,
  size = "small",
  disabled = false,
  error,
  helperText,
  fullWidth,
}: Props) {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const parsed = useMemo(() => {
    const match = /^(\d{1,2}):(\d{1,2})$/.exec(value ?? "");
    if (!match) return null;
    const hour24 = Number(match[1]);
    const minute = Number(match[2]);
    if (!Number.isFinite(hour24) || !Number.isFinite(minute)) return null;
    return { ...to12h(hour24), minute };
  }, [value]);

  const displayValue = useMemo(() => {
    if (!parsed) return "";
    return `${pad2(parsed.hour12)}:${pad2(parsed.minute)} ${parsed.period}`;
  }, [parsed]);

  const hours12 = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);
  const minutes = useMemo(() => {
    const step = minuteStep > 0 ? minuteStep : 1;
    const values = new Set<number>();
    for (let m = 0; m < 60; m += step) values.add(m);
    if (parsed && !values.has(parsed.minute)) values.add(parsed.minute);
    return Array.from(values).sort((a, b) => a - b);
  }, [minuteStep, parsed]);

  const minTotal = toMinutes(minTime);
  const maxTotal = toMinutes(maxTime);
  const clampHHmm = (hhmm: string) => {
    const total = toMinutes(hhmm) ?? 0;
    const bounded = Math.min(Math.max(total, minTotal ?? 0), maxTotal ?? 24 * 60 - 1);
    return `${pad2(Math.floor(bounded / 60))}:${pad2(bounded % 60)}`;
  };
  const outOfRange = (total: number) =>
    (minTotal !== null && total < minTotal) || (maxTotal !== null && total > maxTotal);
  // Uma hora só fica habilitada se ainda tiver algum minuto dentro da faixa.
  const isHourDisabled = (hour12: number, period: Period) => {
    const hour24 = to24h(hour12, period);
    return (
      (minTotal !== null && hour24 * 60 + 59 < minTotal) || (maxTotal !== null && hour24 * 60 > maxTotal)
    );
  };
  const isPeriodDisabled = (period: Period) => {
    const start = period === "AM" ? 0 : 12 * 60;
    return (minTotal !== null && start + 12 * 60 - 1 < minTotal) || (maxTotal !== null && start > maxTotal);
  };

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  // Trocar hora/período pode cair fora da faixa permitida; o valor é sempre ajustado para dentro dela.
  const setHour12 = (hour12: number) =>
    onChange(clampHHmm(`${pad2(to24h(hour12, parsed?.period ?? "AM"))}:${pad2(parsed?.minute ?? 0)}`));
  const setMinute = (minute: number) =>
    onChange(clampHHmm(`${pad2(to24h(parsed?.hour12 ?? 12, parsed?.period ?? "AM"))}:${pad2(minute)}`));
  const setPeriod = (period: Period) =>
    onChange(clampHHmm(`${pad2(to24h(parsed?.hour12 ?? 12, period))}:${pad2(parsed?.minute ?? 0)}`));

  return (
    <>
      <Input
        label={label}
        value={displayValue}
        onClick={handleOpen}
        size={size}
        disabled={disabled}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        inputProps={{ readOnly: true, inputMode: "none" }}
        sx={{ minWidth, marginY: 0, ...sx }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton aria-label={label} edge="end" onClick={handleOpen} disabled={disabled}>
                <AccessTimeOutlined fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 330 }}>
          <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
            <Select
              sx={{ minWidth: 90, marginY: 0 }}
              label={intl.formatMessage({ id: "timePicker.hour" })}
              value={parsed?.hour12 ?? ""}
              onChange={(e) => setHour12(Number(e.target.value))}
              MenuProps={{ disablePortal: true, PaperProps: { sx: { maxHeight: 280 } } }}
            >
              {hours12.map((h) => (
                <MenuItem key={h} value={h} disabled={isHourDisabled(h, parsed?.period ?? "AM")}>
                  {pad2(h)}
                </MenuItem>
              ))}
            </Select>

            <Select
              sx={{ minWidth: 90, marginY: 0 }}
              label={intl.formatMessage({ id: "timePicker.minute" })}
              value={parsed?.minute ?? ""}
              onChange={(e) => setMinute(Number(e.target.value))}
              MenuProps={{ disablePortal: true, PaperProps: { sx: { maxHeight: 280 } } }}
            >
              {minutes.map((m) => (
                <MenuItem
                  key={m}
                  value={m}
                  disabled={outOfRange(to24h(parsed?.hour12 ?? 12, parsed?.period ?? "AM") * 60 + m)}
                >
                  {pad2(m)}
                </MenuItem>
              ))}
            </Select>

            <Select
              sx={{ minWidth: 90, marginY: 0 }}
              label={intl.formatMessage({ id: "timePicker.period" })}
              value={parsed?.period ?? "AM"}
              onChange={(e) => setPeriod(e.target.value as Period)}
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value="AM" disabled={isPeriodDisabled("AM")}>{intl.formatMessage({ id: "timePicker.am" })}</MenuItem>
              <MenuItem value="PM" disabled={isPeriodDisabled("PM")}>{intl.formatMessage({ id: "timePicker.pm" })}</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} justifyContent="space-between">
            <LoadingButton
              size="small"
              onClick={() => {
                if (onClear) onClear();
                else onChange("");
                handleClose();
              }}
            >
              {intl.formatMessage({ id: "datePicker.clear" })}
            </LoadingButton>
            <LoadingButton
              size="small"
              variant="contained"
              onClick={() => {
                if (onNow) onNow();
                else onChange(clampHHmm(nowHHmm()));
                handleClose();
              }}
            >
              {intl.formatMessage({ id: "timePicker.now" })}
            </LoadingButton>
          </Stack>
        </Box>
      </Popover>
    </>
  );
}
